import React, { useCallback, useEffect, useRef, useState } from "react";
import "../../css/Accounts.css";
import { fetchfinanceAccounts } from "../../service/api/adminApi";

import {
    TrendingUp,
    Wallet,
    CreditCard,
    Store,
    Users,
    Landmark,
    ReceiptIndianRupee,
    CircleDollarSign,
    HandCoins,
    CalendarDays,
} from "lucide-react";

const rangeOptions = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "last_7_days", label: "Last 7 Days" },
    { value: "last_30_days", label: "Last 30 Days" },
    { value: "this_month", label: "This Month" },
    { value: "last_month", label: "Last Month" },
    { value: "custom", label: "Custom Range" },
];

const cards = [
    { key: "total_sales", label: "Total Sales", icon: <TrendingUp size={22} />, color: "blue" },
    { key: "total_cod", label: "Total COD", icon: <HandCoins size={22} />, color: "sky" },
    { key: "total_online", label: "Total Online", icon: <CreditCard size={22} />, color: "indigo" },
    { key: "total_debited_from_wallet", label: "Total Debited From Wallet", icon: <Wallet size={22} />, color: "green" },
    { key: "total_added_to_wallet", label: "Total Added To Wallet", icon: <CircleDollarSign size={22} />, color: "blue" },
    { key: "delivery_boy_earnings", label: "Delivery Boy Earnings", icon: <Users size={22} />, color: "sky" },
    { key: "total_restaurant_bills", label: "Total Restaurant Bills", icon: <Store size={22} />, color: "indigo" },
    { key: "other_spendings", label: "Other Spendings", icon: <ReceiptIndianRupee size={22} />, color: "green" },
    { key: "admin_earning", label: "Admin Earning", icon: <Landmark size={22} />, color: "blue" },
];

const pad = (value) => String(value).padStart(2, "0");

const toDateInputValue = (date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const addDays = (date, days) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
};

const addMonths = (date, months) => {
    const next = new Date(date);
    next.setMonth(next.getMonth() + months);
    return next;
};

const getRangeDates = (rangeValue) => {
    const today = new Date();

    switch (rangeValue) {
        case "today":
            return { from: today, to: today };
        case "yesterday": {
            const yesterday = addDays(today, -1);
            return { from: yesterday, to: yesterday };
        }
        case "last_7_days":
            return { from: addDays(today, -6), to: today };
        case "last_30_days":
            return { from: addDays(today, -29), to: today };
        case "last_month": {
            const lastMonth = addMonths(today, -1);
            return {
                from: new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1),
                to: new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0),
            };
        }
        case "this_month":
        default:
            return {
                from: new Date(today.getFullYear(), today.getMonth(), 1),
                to: today,
            };
    }
};

const formatDate = (value) => {
    const date = new Date(value);
    return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
    });
};

export default function Accounts() {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({});
    const [range, setRange] = useState("this_month");
    const [openRange, setOpenRange] = useState(false);
    const [dateLabel, setDateLabel] = useState("This Month");
    const [showCustom, setShowCustom] = useState(false);
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [appliedCustomRange, setAppliedCustomRange] = useState(null);

    const rangeRef = useRef(null);

    const money = (value) => {
        const amount = Number(value || 0);
        return `\u20B9 ${amount.toFixed(2)}`;
    };

    const loadData = useCallback(async () => {
        try {
            setLoading(true);

            const selectedRange =
                range === "custom" && appliedCustomRange
                    ? appliedCustomRange
                    : (() => {
                        const { from, to } = getRangeDates(range);
                        return {
                            from: toDateInputValue(from),
                            to: toDateInputValue(to),
                        };
                    })();

            const res = await fetchfinanceAccounts({
                from: selectedRange.from,
                to: selectedRange.to,
            });

            setData(res?.cards || {});

            if (res?.from_date && res?.to_date && range !== "this_month") {
                setDateLabel(`${formatDate(res.from_date)} - ${formatDate(res.to_date)}`);
            } else if (range === "this_month") {
                setDateLabel("This Month");
            }
        } catch (error) {
            console.error(error);
            alert("Failed to load accounts");
        } finally {
            setLoading(false);
        }
    }, [appliedCustomRange, range]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (rangeRef.current && !rangeRef.current.contains(event.target)) {
                setOpenRange(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleRangeSelect = (item) => {
        if (item.value === "custom") {
            setShowCustom(true);
            return;
        }

        setRange(item.value);
        setAppliedCustomRange(null);
        setDateLabel(item.label);
        setOpenRange(false);
        setShowCustom(false);
    };

    const todayInputValue = toDateInputValue(new Date());

    return (
        <div className="accounts-page">
            <div className="accounts-head">
                <h2>Accounts</h2>

                <div className="accounts-range-wrap" ref={rangeRef}>
                    <button
                        type="button"
                        className="accounts-date-btn"
                        onClick={() => setOpenRange((prev) => !prev)}
                    >
                        <CalendarDays size={16} />
                        <span>{dateLabel}</span>
                    </button>

                    {openRange && (
                        <div className="accounts-range-menu">
                            {rangeOptions.map((item) => (
                                <button
                                    key={item.value}
                                    type="button"
                                    className={`accounts-range-item ${
                                        range === item.value ? "active" : ""
                                    }`}
                                    onClick={() => handleRangeSelect(item)}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {showCustom && (
                        <div className="accounts-custom-box">
                            <input
                                type="date"
                                value={customFrom}
                                max={todayInputValue}
                                onChange={(e) => setCustomFrom(e.target.value)}
                            />

                            <input
                                type="date"
                                value={customTo}
                                min={customFrom || undefined}
                                max={todayInputValue}
                                onChange={(e) => setCustomTo(e.target.value)}
                            />

                            <button
                                type="button"
                                onClick={() => {
                                    if (!customFrom || !customTo) {
                                        alert("Please select from and to dates");
                                        return;
                                    }

                                    if (customFrom > customTo) {
                                        alert("From date cannot be after to date");
                                        return;
                                    }

                                    setRange("custom");
                                    setAppliedCustomRange({ from: customFrom, to: customTo });
                                    setDateLabel(`${formatDate(customFrom)} - ${formatDate(customTo)}`);
                                    setOpenRange(false);
                                    setShowCustom(false);
                                }}
                            >
                                Apply
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="accounts-grid">
                {cards.map((card) => (
                    <div className="accounts-card" key={card.key}>
                        <div>
                            <div className="accounts-value">
                                {money(data[card.key])}
                            </div>
                            <div className="accounts-label">{card.label}</div>
                        </div>

                        <div className={`accounts-icon ${card.color}`}>
                            {loading ? <span className="accounts-spinner" /> : card.icon}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}